/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/no-unused-prop-types */
import React, { RefObject, useState, forwardRef, useMemo, useEffect, useImperativeHandle } from 'react';
import classNames from 'classnames';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import styles from 'src/components/layouts/ProductsSearchTable/ProductSearchTable.module.scss';
import { Option } from 'src/components/ui/Select/Select';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Checkbox from '../../ui/Checkbox/Checkbox';
import Input from '../../ui/Input/Input';
import Select from '../../ui/Select/Select';
import ProductImage from '../../ui/ProductImage/ProductImage';
import { useReturnReasonsQuery } from '../../../services/returns';

export interface ReturnsTableProps {
  containerRef: RefObject<HTMLDivElement>;
  data: any;
  isReturnPage?: boolean;
  onSelectionChange: (updatedData: any) => void;
  readonly?: boolean;
}

export interface ReturnData {
  [productId: string]: {
    return_quantity: number;
    reason_id: number;
    lot_id?: number;
  };
}

export interface FormData {
  returnData: ReturnData;
}

interface ProductLot {
  id: number;
  name: string;
  removal_date?: string;
}

interface ProductRow {
  product_id: string | number;
  quantity?: number;
  reason_id?: number;
  lot?: ProductLot;
}

const columnHelper = createColumnHelper<any>();
interface ColumnHelperProps {
  t: (key: string) => string;
}

export const getSchemaForSelected = (selectedIds: string[] | number[], t: (key: string) => string) => {
  const shape: Record<string, any> = {};
  selectedIds.forEach(id => {
    shape[id] = z.object({
      return_quantity: z.number().min(1, t('returns.quantity_required')),
      reason_id: z.number().optional(),
      lot_id: z.number().optional(),
    });
  });

  return z.object({
    returnData: z.object(shape),
  });
};

const ProductsSearchTable = forwardRef<{ submitForm: (callback?: () => void) => void }, ReturnsTableProps>(
  ({ data, onSelectionChange, readonly = false, isReturnPage }, ref) => {
    const { t } = useTranslation();
    const [rowSelection, setRowSelection] = useState({});
    const { data: reasons } = useReturnReasonsQuery();
    const reasonOptions: Option[] =
      reasons?.map(reason => ({
        label: reason.name,
        value: reason.id.toString(),
      })) || [];

    const selectedProductIds = useMemo(
      () =>
        Object.keys(rowSelection)
          .map(index => data[Number(index)]?.product_id)
          .filter(Boolean),
      [rowSelection, data],
    );

    const dynamicSchema = useMemo(() => getSchemaForSelected(selectedProductIds, t), [selectedProductIds]);

    const typedData = data as ProductRow[];
    const {
      control,
      handleSubmit,
      formState: { errors },
      getValues,
    } = useForm<FormData>({
      resolver: zodResolver(dynamicSchema),
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: {
        returnData: typedData.reduce(
          (acc, row) => {
            acc[row.product_id] = {
              return_quantity: row.quantity ?? 0,
              reason_id: row.reason_id ? +row.reason_id : undefined,
              lot_id: row.lot?.id ?? undefined,
            };
            return acc;
          },
          {} as Record<string | number, { return_quantity: number; reason_id?: number; lot_id?: number }>,
        ),
      },
    });

    const getCurrentSelectedProducts = () => {
      const values = getValues().returnData;
      return selectedProductIds.map(id => ({
        product_id: id,
        quantity: values[id].return_quantity,
        reason_id: +values[id].reason_id,
        lot_id: values[id].lot_id,
      }));
    };

    useImperativeHandle(ref, () => ({
      submitForm: (callback?: () => void) => {
        handleSubmit(() => {
          onSelectionChange(getCurrentSelectedProducts());
          callback?.();
        })();
      },
    }));

    const getColumns = ({ t }: ColumnHelperProps) => [
      columnHelper.accessor(row => row.product_id, {
        id: 'checkbox',
        size: 50,
        enableHiding: false,
        enableSorting: false,
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: info => (
          <Checkbox
            checked={readonly ? true : info.row.getIsSelected()}
            indeterminate={info.row.getIsSomeSelected()}
            onChange={e => {
              info.row.getToggleSelectedHandler()(e);
            }}
            disabled={readonly || info.row.original.quantity === 0}
          />
        ),
      }),
      columnHelper.accessor(row => (isReturnPage ? row.product_name : row.name), {
        id: 'name',
        size: isReturnPage ? 250 : 400,
        meta: {
          label: isReturnPage ? t('returns.product') : t('general.name'),
        },
        header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
        cell: info => (
          <div className="flex items-center gap-[12px]">
            <div className={`${isReturnPage ? 'h-[36px] w-[36px] md:h-[72px] md:w-[72px]' : 'h-[40px] w-[40px]'}`}>
              <ProductImage id={info.row.original.product_id} />
            </div>

            <div className="flex flex-col gap-2">
              <span className="typo-text-14--medium">{info.row.original.product_name}</span>
            </div>
          </div>
        ),
      }),
      columnHelper.accessor(row => row.quantity, {
        id: 'quantity',
        size: 100,
        meta: { label: t('product_packaging.quantity') },
        header: ({ column }) => (
          <span className="typo-text-12 text-faded !text-center">{column.columnDef.meta?.label}</span>
        ),
        cell: info => (
          <div className="flex flex-col gap-y-4">
            <span className="typo-text-14 px-[16px] text-faded"> {info.getValue()}x </span>
          </div>
        ),
      }),
      columnHelper.accessor(row => row.return_quantity, {
        id: 'return_quantity',
        size: 100,
        meta: { label: t('returns.return_amount') },
        header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
        cell: info => {
          const productId = info.row.original.product_id;
          return (
            <div className="flex flex-col minHeight-[30px] h-full w-full max-w-[150px]">
              <Controller
                name={`returnData.${productId}.return_quantity`}
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    max={info.row.original.quantity}
                    placeholder="0"
                    disabled={readonly || !info.row.getIsSelected() || info.row.original.quantity === 0}
                    error={!!fieldState.error}
                    errorMessage={fieldState.error?.message}
                    onChange={e => {
                      const { value } = e.target;
                      const maxQty = info.row.original.quantity;

                      if (value === '') {
                        field.onChange('');
                        return;
                      }

                      let num = Number(value);

                      if (num > maxQty) num = maxQty;
                      if (num < 0) num = 0;

                      field.onChange(num);
                      onSelectionChange(getCurrentSelectedProducts());
                    }}
                  />
                )}
              />
            </div>
          );
        },
      }),
      columnHelper.accessor(row => row.reason, {
        id: 'reason',
        size: 200,
        meta: { label: t('returns.reason') },
        header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
        cell: info => {
          const productId = info.row.original.product_id;
          return (
            <div className="flex flex-col h-[30px] justify-center">
              <Controller
                name={`returnData.${productId}.reason_id`}
                control={control}
                render={({ field }) => {
                  const currentValue = reasonOptions?.find(option => +option.value === +field.value);
                  return (
                    <Select
                      {...field}
                      options={reasonOptions}
                      defaultValue={currentValue || undefined}
                      placeholder={t('general.select')}
                      disabled={readonly || !info.row.getIsSelected() || info.row.original.quantity === 0}
                      onChange={selected => {
                        field.onChange(selected ? +selected.value : undefined);
                        onSelectionChange(getCurrentSelectedProducts());
                      }}
                    />
                  );
                }}
              />
              <span className="typo-text--12 status-error-full">
                {errors?.returnData?.[productId]?.reason_id?.message}
              </span>
            </div>
          );
        },
      }),
    ];

    const rootRef = React.useRef<HTMLDivElement>(null);

    const table = useReactTable({
      data: data || [],
      columns: getColumns({ t }),
      state: { rowSelection },
      enableRowSelection: true,
      onRowSelectionChange: setRowSelection,
      getCoreRowModel: getCoreRowModel(),
    });

    useEffect(() => {
      onSelectionChange(getCurrentSelectedProducts());
    }, [selectedProductIds]);

    return (
      <form>
        <div className={classNames(styles.tableContent, 'w-full')}>
          <div ref={rootRef} className={classNames(styles.root)}>
            <div
              className={classNames(
                styles.tableWrapper,
                `min-h-[20rem] !overflow-visible ${isReturnPage && '!border-none'}`,
                `${!isReturnPage && readonly && 'surface-base'}`,
              )}>
              {isReturnPage && <span className="typo-text-16--bold">{t('returns.your_order')}</span>}
              <table className={`w-full ${isReturnPage && 'mt-[48px]'}`}>
                <thead className={`${isReturnPage && '!bg-transparent'}`}>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th key={header.id} colSpan={header.colSpan} style={{ minWidth: header.getSize() }}>
                          <div className={styles.headerColumn} onClick={header.column.getToggleSortingHandler()}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length > 0 ? (
                    table.getRowModel().rows.map(row => (
                      <tr key={row.id} className={`${!isReturnPage && readonly && 'surface-base'}`}>
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id} className="cursor-pointer">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={table.getAllColumns().length}
                        className="text-center w-full py-4 typo-text-14--bold text-faded">
                        <div className="flex justify-center items-center h-20">{t('general.no_search_results')}</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </form>
    );
  },
);

export default ProductsSearchTable;
